import { Avatar, IconButton, Select } from "@design";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TeamMemberResponse } from "@/api";
import { Text } from "@/components/Text";
import { asTeamRole, TEAM_ROLES } from "./teamRoles";

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();

/** One person in a team: their role (a select when editable) and Remove. */
export function TeamMemberRow({
  member,
  isYou,
  editable,
  removable,
  onlyAdmin,
  onRole,
  onRemove,
}: Readonly<{
  member: TeamMemberResponse;
  isYou: boolean;
  editable: boolean;
  removable: boolean;
  /** The team's only admin: their role can't change until someone else is an admin. */
  onlyAdmin: boolean;
  onRole: (role: string) => void;
  onRemove: () => void;
}>) {
  const { t } = useTranslation("teams");
  const role = asTeamRole(member.role);
  return (
    <li className="flex flex-wrap items-center gap-3 py-2">
      <Avatar initials={initialsOf(member.name)} size="sm" />
      <div className="flex min-w-0 flex-1 flex-col">
        <Text variant="body-sm" className="truncate">
          {isYou ? t("detail.nameYou", { name: member.name }) : member.name}
        </Text>
        <Text variant="caption" tone="tertiary" className="truncate">
          {member.email}
        </Text>
        {editable && onlyAdmin && (
          <Text variant="caption" tone="tertiary">
            {t("detail.lastAdmin")}
          </Text>
        )}
      </div>
      {editable ? (
        <div className="w-36">
          <Select
            size="md"
            aria-label={t("detail.changeRole", { name: member.name })}
            options={TEAM_ROLES.map((r) => ({ value: r, label: t(`roles.${r}`) }))}
            value={role}
            onChange={onRole}
            disabled={onlyAdmin}
          />
        </div>
      ) : (
        <Text variant="caption" tone="secondary">
          {t(`roles.${role}`)}
        </Text>
      )}
      {editable && removable && (
        <IconButton
          icon={<Trash2 />}
          aria-label={t("detail.remove", { name: member.name })}
          variant="ghost"
          size="sm"
          onClick={onRemove}
        />
      )}
    </li>
  );
}
